import { Injectable, OnDestroy } from '@angular/core';
import { CollectionItem, CollectionService } from '@app/services/collection.service';
import { DiscountGroupService, SpecialDiscount2Item } from '@app/services/discount-group.service';
import { CustomerPersonItem, EconomyService, FactoringItem, InvoiceMethodsItem, PaymentTypeItem, PriceListItem, TaxItem, ValutaItem } from '@app/services/economy.service';
import { BusinesscoItem, BusinessItem, CoopItem, CountryItem, DeliveryMethodItem, DeliveryTermItem, DepartmentItem, LogisticService, StockplaceItem } from '@app/services/logistic.service';
import { OrderDialogItem, OrderHeadDeliveryMethodDataResponse, OrderHeadDynamicSpecialGet, OrderHeadGet, OrderheadService, OrderMethodItem, PortalUserItem, SellerItem } from '@app/services/orderhead.service';
import { PickJobGroupItem, PickJobGroupService } from '@app/services/pick-job-group.service';
import { BehaviorSubject, combineLatest, EMPTY, forkJoin, from, merge, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs/operators';

@Injectable()
export class OrderHeadContextService implements OnDestroy {
    private readonly orderHeadSubject = new BehaviorSubject<OrderHeadGet | undefined>(undefined);

    private readonly staticDataRefreshSubject = new BehaviorSubject<void>(undefined);

    private readonly dynamicSpecialRefreshSubject = new BehaviorSubject<void>(undefined);

    private readonly businesscoNoChangeSubject = new Subject<string>();

    private readonly stockplaceIdChangeSubject = new Subject<string>();

    private readonly statCustNoChangeSubject = new Subject<string>();

    private readonly invoiceCountryIdChangeSubject = new Subject<string>();

    private readonly deliveryMethodNoChangeSubject = new Subject<string>();

    public readonly orderHead$ = this.orderHeadSubject.asObservable().pipe(
        filter((get): get is OrderHeadGet => !!get),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public readonly orderInternalNo$ = this.orderHead$.pipe(
        map((get) => this.toId(get.order_internal_no)),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public readonly businesscoNo$ = merge(this.orderHead$.pipe(map((get) => this.toId(get.businessco_no))), this.businesscoNoChangeSubject.asObservable()).pipe(distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));

    public readonly stockplaceId$ = merge(this.orderHead$.pipe(map((get) => this.toId(get.stockplace_id))), this.stockplaceIdChangeSubject.asObservable()).pipe(distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));

    public readonly statCustNo$ = merge(this.orderHead$.pipe(map((get) => this.toId(get.stat_cust_no))), this.statCustNoChangeSubject.asObservable()).pipe(distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));

    public readonly invoiceCountryId$ = merge(this.orderHead$.pipe(map((get) => this.toId(get.invoice_country_id))), this.invoiceCountryIdChangeSubject.asObservable()).pipe(distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));

    public readonly deliveryMethodNo$ = this.deliveryMethodNoChangeSubject.asObservable().pipe(distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }));

    public readonly staticData$ = this.staticDataRefreshSubject.pipe(
        switchMap(() =>
            forkJoin({
                companies: this.loadList(() => this.logistic.getCompanies()),
                portalUsers: this.loadList(() => this.orderhead.getPortalUsers()),
                orderMethods: this.loadList(() => this.orderhead.getOrderMethods()),
                businesses: this.loadList(() => this.logistic.getBusinesses()),
                coops: this.loadList(() => this.logistic.getCoops()),
                countries: this.loadList(() => this.logistic.getCountries()),
                deliveryTerms: this.loadList(() => this.logistic.getDeliveryTerms()),
                payments: this.loadList(() => this.economy.getPaymentTypes()),
                pricelists: this.loadList(() => this.economy.getPriceLists()),
                factorings: this.loadList(() => this.economy.getFactorings()),
                invoiceMethods: this.loadList(() => this.economy.getInvoiceMethods()),
                valutas: this.loadList(() => this.economy.getValutas()),
                discount2s: this.loadList(() => this.discountGroup.getSpecialDiscount2s()),
                collections: this.loadList(() => this.collection.getCollections({ isactive: '1' })),
            }),
        ),
        map((data): OrderHeadStaticData => data),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public readonly companies$ = this.staticData$.pipe(map((data) => data.companies));

    public readonly portalUsers$ = this.staticData$.pipe(map((data) => data.portalUsers));

    public readonly orderMethods$ = this.staticData$.pipe(map((data) => data.orderMethods));

    public readonly businesses$ = this.staticData$.pipe(map((data) => data.businesses));

    public readonly coops$ = this.staticData$.pipe(map((data) => data.coops));

    public readonly countries$ = this.staticData$.pipe(map((data) => data.countries));

    public readonly deliveryTerms$ = this.staticData$.pipe(map((data) => data.deliveryTerms));

    public readonly payments$ = this.staticData$.pipe(map((data) => data.payments));

    public readonly pricelists$ = this.staticData$.pipe(map((data) => data.pricelists));

    public readonly factorings$ = this.staticData$.pipe(map((data) => data.factorings));

    public readonly invoiceMethods$ = this.staticData$.pipe(map((data) => data.invoiceMethods));

    public readonly valutas$ = this.staticData$.pipe(map((data) => data.valutas));

    public readonly discount2s$ = this.staticData$.pipe(map((data) => data.discount2s));

    public readonly collections$ = this.staticData$.pipe(map((data) => data.collections));

    public readonly businesscoData$ = this.businesscoNo$.pipe(
        switchMap((businesscoNo) =>
            businesscoNo
                ? forkJoin({
                      stockplaces: this.loadList(() => this.logistic.getStockplaces({ businessco_no: businesscoNo })),
                      sellers: this.loadList(() => this.orderhead.getSellers({ businessco_no: businesscoNo })),
                      departments: this.loadList(() => this.logistic.getDepartments(businesscoNo)),
                  })
                : of(this.emptyBusinesscoData()),
        ),
        map((data): OrderHeadBusinesscoData => data),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public readonly stockplaces$ = this.businesscoData$.pipe(map((data) => data.stockplaces));

    public readonly sellers$ = this.businesscoData$.pipe(map((data) => data.sellers));

    public readonly departments$ = this.businesscoData$.pipe(map((data) => data.departments));

    public readonly customerPersons$ = this.statCustNo$.pipe(
        switchMap((statCustNo) => (+statCustNo > 0 ? this.loadList(() => this.economy.getCustomerPersons({ cust_no: +statCustNo })) : of([] as CustomerPersonItem[]))),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public readonly pickJobGroups$ = this.stockplaceId$.pipe(
        switchMap((stockplaceId) => (stockplaceId ? this.loadList(() => this.pickJobGroup.getPickJobGroups({ stockplace_id: stockplaceId })) : of([] as PickJobGroupItem[]))),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public readonly taxes$ = this.invoiceCountryId$.pipe(
        switchMap((countryId) => (countryId ? this.loadList(() => this.economy.getTaxes({ country_id: countryId })) : of([] as TaxItem[]))),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public readonly deliveryMethods$ = this.orderInternalNo$.pipe(
        switchMap((orderInternalNo) => (orderInternalNo ? this.loadList(() => this.logistic.getDeliveryMethods({ order_internal_no: orderInternalNo })) : of([] as DeliveryMethodItem[]))),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public readonly deliveryMethodData$ = this.deliveryMethodNo$.pipe(
        switchMap((deliveryMethodNo) =>
            deliveryMethodNo
                ? from(this.orderhead.getOrderHeadDeliveryMethodData({ deliverymethod_no: deliveryMethodNo })).pipe(
                      catchError((error) => {
                          console.error(error);
                          return EMPTY;
                      }),
                  )
                : EMPTY,
        ),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public readonly dynamicSpecialOrderHead$ = combineLatest([this.orderHead$, this.dynamicSpecialRefreshSubject]).pipe(
        filter(([get]) => get.setting_oh_show_dynamic_special === '1'),
        switchMap(([get]) =>
            from(this.orderhead.getDyanmicSpecialOrderHead({ order_internal_no: get.order_internal_no })).pipe(
                map((dynamicSpecial) => ({ ...dynamicSpecial, ...get }) as OrderHeadDynamicSpecialOrderHead),
                catchError((error) => {
                    console.error(error);
                    return of(get as OrderHeadDynamicSpecialOrderHead);
                }),
            ),
        ),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public readonly orderDialogs$ = this.orderInternalNo$.pipe(
        switchMap((orderInternalNo) => (orderInternalNo ? this.loadList(() => this.orderhead.getOrderDialog({ order_internal_no: orderInternalNo })) : of([] as OrderDialogItem[]))),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    constructor(
        private logistic: LogisticService,
        private orderhead: OrderheadService,
        private economy: EconomyService,
        private pickJobGroup: PickJobGroupService,
        private discountGroup: DiscountGroupService,
        private collection: CollectionService,
    ) {}

    public setOrderHead(get?: OrderHeadGet) {
        this.orderHeadSubject.next(get);
    }

    public setBusinesscoNo(value?: string | number | null) {
        this.businesscoNoChangeSubject.next(this.toId(value));
    }

    public setStockplaceId(value?: string | number | null) {
        this.stockplaceIdChangeSubject.next(this.toId(value));
    }

    public setStatCustNo(value?: string | number | null) {
        this.statCustNoChangeSubject.next(this.toId(value));
    }

    public setInvoiceCountryId(value?: string | number | null) {
        this.invoiceCountryIdChangeSubject.next(this.toId(value));
    }

    public setDeliveryMethodNo(value?: string | number | null) {
        this.deliveryMethodNoChangeSubject.next(this.toId(value));
    }

    public refreshStaticData() {
        this.staticDataRefreshSubject.next();
    }

    public refreshDynamicSpecialData() {
        this.dynamicSpecialRefreshSubject.next();
    }

    private loadList<T>(factory: () => Promise<T[]>): Observable<T[]> {
        return from(factory()).pipe(
            catchError((error) => {
                console.error(error);
                return of([] as T[]);
            }),
        );
    }

    private toId(value?: string | number | null): string {
        return value === undefined || value === null ? '' : '' + value;
    }

    private emptyBusinesscoData(): OrderHeadBusinesscoData {
        return {
            stockplaces: [],
            sellers: [],
            departments: [],
        };
    }

    ngOnDestroy(): void {
        this.orderHeadSubject.complete();
        this.staticDataRefreshSubject.complete();
        this.dynamicSpecialRefreshSubject.complete();
        this.businesscoNoChangeSubject.complete();
        this.stockplaceIdChangeSubject.complete();
        this.statCustNoChangeSubject.complete();
        this.invoiceCountryIdChangeSubject.complete();
        this.deliveryMethodNoChangeSubject.complete();
    }
}

export interface OrderHeadStaticData {
    companies: BusinesscoItem[];
    portalUsers: PortalUserItem[];
    orderMethods: OrderMethodItem[];
    businesses: BusinessItem[];
    coops: CoopItem[];
    countries: CountryItem[];
    deliveryTerms: DeliveryTermItem[];
    payments: PaymentTypeItem[];
    pricelists: PriceListItem[];
    factorings: FactoringItem[];
    invoiceMethods: InvoiceMethodsItem[];
    valutas: ValutaItem[];
    discount2s: SpecialDiscount2Item[];
    collections: CollectionItem[];
}

export interface OrderHeadBusinesscoData {
    stockplaces: StockplaceItem[];
    sellers: SellerItem[];
    departments: DepartmentItem[];
}

export type OrderHeadDeliveryMethodData = OrderHeadDeliveryMethodDataResponse;

export type OrderHeadDynamicSpecialOrderHead = OrderHeadDynamicSpecialGet & OrderHeadGet;
